<template>
  <div class="w-full">
    <label
      v-if="label"
      :for="selectId"
      class="text-navy-700 mb-1.5 block text-sm font-medium"
    >
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>
    <div class="relative">
      <select
        :id="selectId"
        ref="selectRef"
        v-model="model"
        :disabled="disabled"
        :required="required"
        :class="selectClasses"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
      >
        <option v-if="placeholder" value="" disabled>
          {{ placeholder }}
        </option>
        <option
          v-for="option in normalizedOptions"
          :key="option.value"
          :value="option.value"
          :disabled="option.disabled"
        >
          {{ option.label }}
        </option>
      </select>
      <div
        class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
      >
        <i class="pi pi-chevron-down text-navy-400" />
      </div>
    </div>
    <p v-if="error" class="mt-1.5 text-sm text-red-600">
      {{ error }}
    </p>
    <p v-else-if="hint" class="text-navy-400 mt-1.5 text-sm">
      {{ hint }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, useId } from "vue"

interface SelectOption {
  label: string
  value: string | number
  disabled?: boolean
}

type OptionInput = SelectOption | string

interface Props {
  label?: string
  placeholder?: string
  options: OptionInput[]
  disabled?: boolean
  required?: boolean
  error?: string
  hint?: string
}

const props = withDefaults(defineProps<Props>(), {
  label: undefined,
  placeholder: undefined,
  disabled: false,
  required: false,
  error: undefined,
  hint: undefined,
})

defineEmits<{
  blur: [event: FocusEvent]
  focus: [event: FocusEvent]
}>()

const model = defineModel<string | number>()
const selectRef = ref<HTMLSelectElement | null>(null)
const selectId = useId()

const normalizedOptions = computed<SelectOption[]>(() =>
  props.options.map((opt) =>
    typeof opt === "string" ? { label: opt, value: opt } : opt,
  ),
)

const selectClasses = computed(() => [
  "block w-full appearance-none rounded-md border bg-white px-3 py-2 pr-10 text-navy-800 transition-colors duration-200",
  "focus:outline-none focus:ring-2 focus:ring-offset-0",
  "disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:text-navy-400",
  props.error
    ? "border-red-300 focus:border-red-500 focus:ring-red-500/20"
    : "border-neutral-300 focus:border-green-500 focus:ring-green-500/20",
])

const focus = () => selectRef.value?.focus()
const blur = () => selectRef.value?.blur()

defineExpose({ focus, blur, selectRef })
</script>
